/**
 * Company (C6, decision 844; help `cloud.company-documents` and `cloud.settings`): what was "Company and documents"
 * and "Settings", as tabs. Tenant admins, managers and office staff see "Details and payment", "Documents and
 * clauses" and "QC defaults" (as Company and documents was, each tab's controls as each role had them); tenant admins
 * also see "Settings" (logo size, remote support, public air traffic), as the Settings page was tenant admins' only.
 * "Final report" (decision 885; help `cloud.final-report-text`) holds the company's standard text and equipment list:
 * written by tenant admins, managers and data processors (data processors open Company for this tab only), read by
 * office staff.
 * "Teslyn Support" (decision 800): tenant admins grant and end the support team's access, managers read the record.
 * "Emails sent" (MB, 2 Nov 2026; help `cloud.email-log`): tenant admins see every email Teslyn has sent for the
 * organisation (they alone read the whole outbox, app-email.md §4); a tab, not a menu item, as decision 844 cut the
 * menu down.
 */
import { useEffect, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { PageHeader } from '../components/States.tsx';
import { TabPanel, Tabs } from '../components/Tabs.tsx';
import { EmailLogPanel } from '../email/EmailLogPanel.tsx';
import { DocumentSettings } from '../quotes/DocumentSettings.tsx';
import { SettingsScreen } from '../settings/SettingsScreen.tsx';
import { FinalReportTextPanel } from '../finalReport/FinalReportTextPanel.tsx';
import { SupportAccessPanel } from '../support/SupportAccessPanel.tsx';
import type { Tenant } from '../tenant/tenant.ts';

export type CompanyTab = 'details' | 'clauses' | 'qc' | 'final-report' | 'settings' | 'support' | 'emails';

/** Who has the quote and contract tabs (Company and documents, as before decision 885). */
const DOCUMENT_ROLES: readonly string[] = ['tenant_admin', 'manager', 'office_staff'];

export interface CompanyScreenProps {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly tenantSlug: string;
  readonly tenantName: string;
  readonly tenant: Tenant;
  readonly userId: string;
  readonly roles: readonly string[];
  /** The tab it opens on (a link to the old Settings page opens "settings"). */
  readonly initialTab?: string | null;
  /** The tab shown (the help panel follows it). */
  readonly onTabChange?: (tab: CompanyTab) => void;
}

export function companyTabs(roles: readonly string[]): ReadonlyArray<readonly [CompanyTab, string]> {
  const documents = roles.some((r) => DOCUMENT_ROLES.includes(r));
  return [
    ...(documents
      ? ([
          ['details', 'Details and payment'],
          ['clauses', 'Documents and clauses'],
          ['qc', 'QC defaults'],
        ] as const)
      : []),
    ['final-report', 'Final report'],
    ...(roles.includes('tenant_admin') ? ([['settings', 'Settings']] as const) : []),
    ...(roles.includes('tenant_admin') || roles.includes('manager')
      ? ([['support', 'Teslyn Support']] as const)
      : []),
    ...(roles.includes('tenant_admin') ? ([['emails', 'Emails sent']] as const) : []),
  ];
}

export function CompanyScreen({
  sb,
  tenantId,
  tenantSlug,
  tenantName,
  tenant,
  userId,
  roles,
  initialTab,
  onTabChange,
}: CompanyScreenProps): ReactElement {
  const tabs = companyTabs(roles);
  const [tab, setTab] = useState<CompanyTab>(
    () => tabs.find(([id]) => id === initialTab)?.[0] ?? tabs[0]![0],
  );
  useEffect(() => onTabChange?.(tab), [tab, onTabChange]);
  return (
    <section className="page">
      <PageHeader title="Company" subtitle={`${tenantName}'s own details and set-up.`} />
      <Tabs label="Company sections" tabs={tabs} current={tab} onChange={setTab} idPrefix="company" />
      <TabPanel idPrefix="company" current={tab}>
        {tab === 'settings' ? (
          <SettingsScreen sb={sb} tenantId={tenantId} roles={roles} tenant={tenant} embedded />
        ) : tab === 'final-report' ? (
          <FinalReportTextPanel
            sb={sb}
            tenantId={tenantId}
            tenantSlug={tenantSlug}
            projectId={null}
            roles={roles}
          />
        ) : tab === 'support' ? (
          <SupportAccessPanel sb={sb} tenantId={tenantId} tenantName={tenantName} roles={roles} />
        ) : tab === 'emails' ? (
          <EmailLogPanel sb={sb} tenantId={tenantId} />
        ) : (
          // one element for the three document tabs, so moving between them keeps what was typed
          <DocumentSettings
            sb={sb}
            tenantId={tenantId}
            tenantSlug={tenantSlug}
            tenantName={tenantName}
            userId={userId}
            roles={roles}
            part={tab}
          />
        )}
      </TabPanel>
    </section>
  );
}
